/**
 * Segment Management screen — exact parity with frontend's Project.Segments/SegmentProfile.
 * Two tabs: Segment Profile (inline editing) + Avatar Settings (HeyGen + Voice).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Edit as EditIcon, Delete as DeleteIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  ImageCropUpload,
  SegmentPersona,
  SegmentChatConfig,
  Tabs,
  AvatarAppearance,
  VoiceSettings,
  AvatarPreviewModal,
} from '@trinity/chat-components';
import type { Tab, AvatarOption } from '@trinity/chat-components';
import { ChooseAvatarModal } from '@trinity/chat-components/components/AvatarSelection/index.js';
import type { TTSConfig } from '@trinity/chat';
import { getAuthenticatedClient } from '@/api/client';

function client() { return getAuthenticatedClient(); }

type AvatarOptionWithGender = AvatarOption & { gender: 'male' | 'female' };

const GENDER_DEFAULT_VOICE: Record<'male' | 'female', string> = { male: 'Matthew', female: 'Joanna' };

const HEYGEN_AVATARS: AvatarOptionWithGender[] = [
  { id: '7b888024-f8c9-4205-95e1-78ce01497bda', imageUrl: '/images/avatar-presets/avatar_shawn.png', label: 'Shawn', gender: 'male' },
  { id: '567e8371-f69f-49ec-9f2d-054083431165', imageUrl: '/images/avatar-presets/avatar_ann.png', label: 'Ann', gender: 'female' },
  { id: '5761a14c-8720-4ce1-8c2b-3f351718fc79', imageUrl: '/images/avatar-presets/avatar_dexter.png', label: 'Dexter', gender: 'male' },
  { id: '0f563214-1cb5-4dc0-a2f9-43f44e5e6b57', imageUrl: '/images/avatar-presets/avatar_judy.png', label: 'Judy', gender: 'female' },
  { id: '40b4f000-f783-4bba-a327-ea58b1a6fdf2', imageUrl: '/images/avatar-presets/avatar_amina.png', label: 'Amina', gender: 'female' },
  { id: '91342979-4c4c-44f1-bd3b-1c846d20341e', imageUrl: '/images/avatar-presets/avatar_anthony.png', label: 'Anthony', gender: 'male' },
  { id: '5dd4d830-957a-419f-9334-0dc4399ada5d', imageUrl: '/images/avatar-presets/avatar_rika.png', label: 'Rika', gender: 'female' },
  { id: '3f291b22-0267-4fb6-a25b-847fb63604b0', imageUrl: '/images/avatar-presets/avatar_anastasia.png', label: 'Anastasia', gender: 'female' },
  { id: 'bf00036b-558a-44b5-b2ff-1e3cec0f4ceb', imageUrl: '/images/avatar-presets/avatar_marianne.png', label: 'Marianne', gender: 'female' },
];

interface SegmentData {
  id: number; identifier: string; projectId: number; name: string; description: string | null;
  persona: string | null; initialMessage: string | null; questions: string[] | null;
  metadata: { title?: string; subtitle?: string; attributes?: string[]; sampleData?: { segmentN: number; totalN: number } };
  dashboardPageId: number | null; iconUrl: string | null; isPublished: boolean;
  ttsSettings?: { avatarId?: string; ttsConfig?: TTSConfig | null } | null;
}

export type SegmentManageScreenProps = {
  /** Override for the back-button target. Defaults to '/segments'. */
  backTo?: string;
};

export function SegmentManageScreen({
  backTo = '/segments',
}: SegmentManageScreenProps = {}): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const segmentId = Number(id);

  const [segment, setSegment] = useState<SegmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'profile' | 'avatar'>('profile');

  // Editable fields
  const [subtitle, setSubtitle] = useState('');
  const [segmentN, setSegmentN] = useState('');
  const [totalN, setTotalN] = useState('');
  const [attributes, setAttributes] = useState<string[]>([]);
  const [newAttribute, setNewAttribute] = useState('');

  // Edit mode states
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false);
  const [isEditingSampleData, setIsEditingSampleData] = useState(false);
  const [isEditingAttributes, setIsEditingAttributes] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedSubtitle, setEditedSubtitle] = useState('');
  const [editedSegmentN, setEditedSegmentN] = useState('');
  const [editedTotalN, setEditedTotalN] = useState('');
  const [editedAttributes, setEditedAttributes] = useState<string[]>([]);

  // Icon upload
  const [iconUploadModalOpen, setIconUploadModalOpen] = useState(false);
  const [iconSaving, setIconSaving] = useState(false);

  // Avatar
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | undefined>();
  const [voiceId, setVoiceId] = useState('Joanna');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [ttsConfig, setTtsConfig] = useState<TTSConfig | null>(null);
  const [avatarSaving, setAvatarSaving] = useState(false);

  const fetchSegment = useCallback(() => {
    client().get(`/segments/${segmentId}`)
      .then((res) => { setSegment(res.data.segment); setLoading(false); })
      .catch(() => setLoading(false));
  }, [segmentId]);

  useEffect(() => { fetchSegment(); }, [fetchSegment]);

  useEffect(() => {
    if (!segment) return;
    setSubtitle(segment.metadata.subtitle ?? '');
    setAttributes(segment.metadata.attributes ?? []);
    setSegmentN(segment.metadata.sampleData?.segmentN?.toString() ?? '');
    setTotalN(segment.metadata.sampleData?.totalN?.toString() ?? '');
    setTtsConfig((segment.ttsSettings?.ttsConfig as TTSConfig) ?? null);
    setSelectedAvatarId(segment.ttsSettings?.avatarId);
    if (segment.ttsSettings?.ttsConfig && (segment.ttsSettings.ttsConfig as { type?: string }).type === 'polly') {
      setVoiceId(((segment.ttsSettings.ttsConfig as { settings?: { voiceId?: string } }).settings?.voiceId) ?? 'Joanna');
    }
  }, [segment]);

  // ── Save helpers ──────────────────────────────────────────────────────
  const saveMetadata = useCallback(async (updates: Partial<SegmentData['metadata']>) => {
    const full = { ...(segment?.metadata ?? {}), ...updates };
    await client().put(`/segments/${segmentId}`, { metadata: full });
    fetchSegment();
  }, [segment, segmentId, fetchSegment]);

  const saveDescription = useCallback(async (desc: string) => {
    await client().put(`/segments/${segmentId}`, { description: desc });
    fetchSegment();
  }, [segmentId, fetchSegment]);

  const savePersona = useCallback(async (persona: string | null) => {
    await client().put(`/segments/${segmentId}`, { persona });
    fetchSegment();
  }, [segmentId, fetchSegment]);

  const saveChatConfig = useCallback(async (data: { initialMessage: string | null; questions: string[] | null }) => {
    await client().put(`/segments/${segmentId}`, data);
    fetchSegment();
  }, [segmentId, fetchSegment]);

  const saveTtsSettings = useCallback(async (avatarId?: string, config?: TTSConfig | null) => {
    await client().put(`/segments/${segmentId}`, { ttsSettings: { avatarId, ttsConfig: config } });
    fetchSegment();
  }, [segmentId, fetchSegment]);

  // ── Inline edit handlers ──────────────────────────────────────────────
  const handleSaveDescription = () => { saveDescription(editedDescription); setIsEditingDescription(false); };
  const handleSaveSubtitle = () => { setSubtitle(editedSubtitle); saveMetadata({ subtitle: editedSubtitle }); setIsEditingSubtitle(false); };
  const handleSaveSampleData = () => {
    setSegmentN(editedSegmentN); setTotalN(editedTotalN);
    saveMetadata({ sampleData: { segmentN: parseInt(editedSegmentN) || 0, totalN: parseInt(editedTotalN) || 0 } });
    setIsEditingSampleData(false);
  };
  const handleAddAttribute = () => { if (newAttribute.trim()) { setEditedAttributes([...editedAttributes, newAttribute.trim()]); setNewAttribute(''); } };
  const handleSaveAttributes = () => { setAttributes(editedAttributes); saveMetadata({ attributes: editedAttributes }); setIsEditingAttributes(false); };
  const handleDeleteAttribute = (i: number) => setEditedAttributes(editedAttributes.filter((_, idx) => idx !== i));

  // ── Avatar handlers ───────────────────────────────────────────────────
  const handleSaveAvatar = useCallback(async (avatarId: string, imageUrl: string) => {
    setAvatarSaving(true);
    try {
      // Save avatar image as icon URL
      await client().put(`/segments/${segmentId}/icon`, { iconUrl: imageUrl });
      // Save avatar ID + TTS config
      await saveTtsSettings(avatarId, ttsConfig);
      setSelectedAvatarId(avatarId);
      const av = HEYGEN_AVATARS.find(a => a.id === avatarId);
      if (av) setVoiceId(GENDER_DEFAULT_VOICE[av.gender]);
      fetchSegment();
    } finally { setAvatarSaving(false); }
  }, [segmentId, ttsConfig, saveTtsSettings, fetchSegment]);

  const handleSaveVoice = useCallback(async (config: TTSConfig) => {
    await saveTtsSettings(selectedAvatarId, config);
    setTtsConfig(config);
  }, [selectedAvatarId, saveTtsSettings]);

  const handleSaveIcon = useCallback(async () => {
    // Icon upload would go through S3 — for now just close modal
    setIconUploadModalOpen(false);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Delete this segment?')) return;
    await client().delete(`/segments/${segmentId}`);
    navigate(backTo);
  }, [segmentId, navigate, backTo]);

  if (loading) return <Root><LoadingText>Loading...</LoadingText></Root>;
  if (!segment) return <Root><LoadingText>Segment not found</LoadingText></Root>;

  // ── Profile Tab ───────────────────────────────────────────────────────
  const profileContent = (
    <>
      <SegmentHeader>
        <SegName>{segment.name}</SegName>
        <DescContainer>
          {isEditingDescription ? (
            <InputWithIcon>
              <DescInput value={editedDescription} onChange={e => setEditedDescription(e.target.value)} rows={3} />
              <EditActions>
                <IconBtn onClick={handleSaveDescription}><CheckIcon sx={{ fontSize: 16 }} /></IconBtn>
                <IconBtn onClick={() => setIsEditingDescription(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconBtn>
              </EditActions>
            </InputWithIcon>
          ) : (
            <InputWithIcon>
              <DescBox>{segment.description || <Placeholder>Add a description</Placeholder>}</DescBox>
              <IconBtn onClick={() => { setEditedDescription(segment.description ?? ''); setIsEditingDescription(true); }}><EditIcon sx={{ fontSize: 18 }} /></IconBtn>
            </InputWithIcon>
          )}
        </DescContainer>
      </SegmentHeader>

      <ProfileSection>
        <SectionTitle>Profile</SectionTitle>

        <Field>
          <FieldLabel>Subtitle</FieldLabel>
          <InputWithIcon>
            <FieldInput value={isEditingSubtitle ? editedSubtitle : subtitle} onChange={e => setEditedSubtitle(e.target.value)} readOnly={!isEditingSubtitle} placeholder="Enter subtitle" />
            {!isEditingSubtitle ? (
              <IconBtn onClick={() => { setEditedSubtitle(subtitle); setIsEditingSubtitle(true); }}><EditIcon sx={{ fontSize: 18 }} /></IconBtn>
            ) : (
              <EditActions>
                <IconBtn onClick={handleSaveSubtitle}><CheckIcon sx={{ fontSize: 16 }} /></IconBtn>
                <IconBtn onClick={() => setIsEditingSubtitle(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconBtn>
              </EditActions>
            )}
          </InputWithIcon>
        </Field>

        <Field>
          <FieldLabel>Attributes</FieldLabel>
          {isEditingAttributes && (
            <InputWithIcon>
              <FieldInput value={newAttribute} onChange={e => setNewAttribute(e.target.value)} placeholder="Enter attribute" onKeyDown={e => { if (e.key === 'Enter') handleAddAttribute(); }} />
              <EditActions>
                <IconBtn onClick={() => { if (newAttribute.trim()) handleAddAttribute(); else handleSaveAttributes(); }}><CheckIcon sx={{ fontSize: 16 }} /></IconBtn>
                <IconBtn onClick={() => { setEditedAttributes([...attributes]); setNewAttribute(''); setIsEditingAttributes(false); }}><CloseIcon sx={{ fontSize: 16 }} /></IconBtn>
              </EditActions>
            </InputWithIcon>
          )}
          <AttrWithIcon>
            <AttrRow>
              {(isEditingAttributes ? editedAttributes : attributes).map((attr, i) => (
                <AttrChip key={i} onClick={isEditingAttributes ? () => handleDeleteAttribute(i) : undefined} $editable={isEditingAttributes}>
                  {attr} {isEditingAttributes && <CloseIcon sx={{ fontSize: 12, ml: 0.5 }} />}
                </AttrChip>
              ))}
              {!isEditingAttributes && attributes.length === 0 && <Placeholder>Add attributes</Placeholder>}
            </AttrRow>
            {!isEditingAttributes && (
              <IconBtn onClick={() => { setEditedAttributes([...attributes]); setNewAttribute(''); setIsEditingAttributes(true); }}><EditIcon sx={{ fontSize: 18 }} /></IconBtn>
            )}
          </AttrWithIcon>
        </Field>

        <Field>
          <FieldLabel>Sample Data</FieldLabel>
          <InputWithIcon style={{ alignItems: 'flex-start' }}>
            <SampleInputs>
              <FieldInput type="number" value={isEditingSampleData ? editedSegmentN : segmentN} onChange={e => setEditedSegmentN(e.target.value)} readOnly={!isEditingSampleData} placeholder="Segment N" min="0" />
              <FieldInput type="number" value={isEditingSampleData ? editedTotalN : totalN} onChange={e => setEditedTotalN(e.target.value)} readOnly={!isEditingSampleData} placeholder="Total N" min="0" />
            </SampleInputs>
            {!isEditingSampleData ? (
              <IconBtn onClick={() => { setEditedSegmentN(segmentN); setEditedTotalN(totalN); setIsEditingSampleData(true); }}><EditIcon sx={{ fontSize: 18 }} /></IconBtn>
            ) : (
              <EditActions>
                <IconBtn onClick={handleSaveSampleData}><CheckIcon sx={{ fontSize: 16 }} /></IconBtn>
                <IconBtn onClick={() => setIsEditingSampleData(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconBtn>
              </EditActions>
            )}
          </InputWithIcon>
        </Field>
      </ProfileSection>

      <SegmentPersona persona={segment.persona} onSave={savePersona} isSaving={false} />
      <SegmentChatConfig initialMessage={segment.initialMessage} questions={segment.questions} onSave={saveChatConfig} isSaving={false} />
    </>
  );

  // ── Avatar Tab ────────────────────────────────────────────────────────
  // Voice settings hidden until we wire up a usable TTS provider for
  // hackathon segments. Avatar appearance stays.
  const avatarContent = (
    <AvatarTabContainer>
      <AvatarAppearance
        avatarUrl={segment.iconUrl}
        onPreview={() => setPreviewOpen(true)}
        onChangeAvatar={() => setAvatarModalOpen(true)}
      />
      {/* <VoiceSettings
        value={ttsConfig}
        onSave={handleSaveVoice}
        voiceId={voiceId}
        onVoiceIdChange={setVoiceId}
      /> */}
    </AvatarTabContainer>
  );

  return (
    <Root>
      {/* Header card with actions */}
      <Card>
        <CardHeader>
          <HeaderTitle>Segment Management</HeaderTitle>
          <HeaderActions>
            <ActionBtn onClick={() => navigate(`${backTo}/${segmentId}`)}>Go to Dashboard</ActionBtn>
            <ActionBtn $danger onClick={handleDelete}>Delete</ActionBtn>
          </HeaderActions>
        </CardHeader>
      </Card>

      {/* Content card with tabs */}
      <Card>
        <CardContent>
          <BackBtn onClick={() => navigate(backTo)}>&larr; Back to Segments</BackBtn>
          <Tabs
            tabs={[
              { id: 'profile' as const, label: 'Segment Profile', content: profileContent },
              { id: 'avatar' as const, label: 'Avatar Settings', content: avatarContent },
            ]}
            activeTab={activeTab}
            onTabChange={(tabId: 'profile' | 'avatar') => setActiveTab(tabId)}
          />
        </CardContent>
      </Card>

      <ImageCropUpload
        open={iconUploadModalOpen}
        onClose={() => setIconUploadModalOpen(false)}
        onSave={handleSaveIcon}
        title="Upload Segment Icon"
        currentImageUrl={segment.iconUrl}
        aspectRatio={1}
        isSaving={iconSaving}
      />
      <ChooseAvatarModal
        open={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        onSave={handleSaveAvatar}
        avatarOptions={HEYGEN_AVATARS}
        selectedAvatarId={selectedAvatarId}
        isSaving={avatarSaving}
      />
      <AvatarPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        avatarId={selectedAvatarId}
        ttsConfig={ttsConfig}
      />
    </Root>
  );
}

// ─── Styled — matches frontend SegmentProfile.css ───────────────────────────

const Root = styled.div`display: flex; flex-direction: column; height: 100%; max-width: 1280px; margin: 0 auto; padding: 0 24px 48px;`;
const Card = styled.div`background: #fff; border: 1px solid var(--sec-01, #e5e5e5); border-radius: 5px; margin-bottom: 0;`;
const CardHeader = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 10px 16px; border-bottom: 1px solid var(--gray-l, #e5e5e5);`;
const HeaderTitle = styled.div`font-size: 20px; font-weight: 600; color: #323232; display: flex; align-items: center; gap: 8px;`;
const HeaderActions = styled.div`display: flex; gap: 8px;`;
const ActionBtn = styled.button<{ $danger?: boolean }>`
  padding: 8px 16px; border-radius: 4px; font-size: 14px; font-weight: 600; font-family: inherit; cursor: pointer;
  border: 1px solid ${p => p.$danger ? 'rgba(211,47,47,0.5)' : 'var(--pri-02, #28AAE1)'};
  color: ${p => p.$danger ? '#d32f2f' : 'var(--pri-02, #28AAE1)'}; background: transparent;
  &:hover { background: ${p => p.$danger ? 'rgba(211,47,47,0.04)' : 'rgba(40,170,225,0.04)'}; }
`;
const CardContent = styled.div`flex: 1; padding: 20px; overflow-y: auto; max-width: 900px;`;
const BackBtn = styled.button`background: none; border: none; color: var(--hyperlink, #28AAE1); cursor: pointer; font-size: 14px; font-family: inherit; padding: 8px 0; &:hover { text-decoration: underline; }`;
const LoadingText = styled.div`display: flex; align-items: center; justify-content: center; min-height: 300px; color: #adadad;`;

const SegmentHeader = styled.div`margin-bottom: 32px;`;
const SegName = styled.h1`font-size: 24px; font-weight: 600; margin: 0 0 16px; color: var(--gray-d, #323232);`;
const DescContainer = styled.div`display: flex; flex-direction: column;`;
const DescBox = styled.div`background: #f1f1f1; border: 1px solid rgba(0,0,0,0.12); border-radius: 4px; font-size: 14px; line-height: 1.6; color: var(--gray-d, #323232); flex: 1; padding: 10px 12px;`;
const DescInput = styled.textarea`padding: 16px; font-size: 14px; line-height: 1.6; border: 1px solid var(--gray-l, #e5e5e5); border-radius: 4px; color: var(--gray-d, #323232); resize: vertical; font-family: inherit; flex: 1; min-height: 80px; outline: none; &:focus { border-color: var(--pri-02, #28AAE1); }`;
const InputWithIcon = styled.div`display: flex; align-items: center; gap: 8px; width: 100%;`;
const EditActions = styled.div`display: flex; align-items: center; gap: 4px; flex-shrink: 0;`;
const IconBtn = styled.div`
  border-radius: 50%; border: 2px solid transparent; padding: 5px; cursor: pointer; display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; flex-shrink: 0; transition: all 0.2s;
  &:hover { border-color: var(--pri-02, #28AAE1); background: var(--pri-02, #28AAE1); }
  &:hover svg { color: #fff; }
  svg { color: var(--pri-02, #28AAE1); transition: all 0.2s; }
`;
const Placeholder = styled.span`color: var(--gray-m, #adadad); font-style: italic;`;

const ProfileSection = styled.div`margin-top: 24px;`;
const SectionTitle = styled.h2`font-size: 18px; font-weight: 700; margin: 0 0 24px; color: var(--gray-d, #323232);`;
const Field = styled.div`margin-bottom: 24px;`;
const FieldLabel = styled.label`display: block; font-size: 14px; font-weight: 700; margin-bottom: 8px; color: var(--gray-d, #323232);`;
const FieldInput = styled.input`
  padding: 10px 12px; font-size: 14px; font-family: inherit; border: 1px solid var(--gray-l, #e5e5e5); border-radius: 4px; color: var(--gray-d, #323232); flex: 1; outline: none;
  &:focus { border-color: var(--pri-02, #28AAE1); } &::placeholder { color: var(--gray-m, #adadad); } &[readonly] { background: #f1f1f1; cursor: default; }
`;
const SampleInputs = styled.div`display: flex; flex-direction: column; gap: 12px; flex: 1;`;
const AttrWithIcon = styled.div`display: flex; align-items: center; gap: 8px; width: 100%;`;
const AttrRow = styled.div`display: flex; flex-wrap: wrap; gap: 8px; flex: 1;`;
const AttrChip = styled.span<{ $editable?: boolean }>`
  height: 32px; padding: 0 8px; font-size: 12px; border-radius: 12px; display: inline-flex; align-items: center;
  background: var(--pri-02, #28AAE1); color: #fff; ${p => p.$editable && 'cursor: pointer;'}
`;
const AvatarTabContainer = styled.div`padding: 24px;`;
